import PropTypes from 'prop-types';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { memo } from 'react';
import { useIntl } from 'react-intl';
import { StandaloneTemplate } from './template/StandaloneTemplate';
import { useAuth } from 'hook/useAuth';
import SignIn, { RoleEnum } from 'pages/authentication/sign-in';
import { getUrlParameter } from 'utils/urlUtils';
import app from 'config/app.json';
import { ROLE_OPERATION_READ } from 'utils/userUtils';
import Error from 'pages/error';
import { SidenavTemplate } from 'pages/template/SidenavTemplate';
import { ChatBoxPublic } from 'pages/chatbox/public';

AuthorizationChecker.propTypes = {
  roleFunctionality: PropTypes.string,
  roleOperation: PropTypes.string,
  children: PropTypes.node
};

/**
 * AuthorizationChecker component checks if a user has the required roles and functionalities.
 * If not, it redirects them either to the signIn page or the home page.
 * Otherwise, it renders the children components.
 *
 * @component
 * @prop {ReactNode} children - The children components to render.
 * @prop {string} roleFunctionality - The required role functionality for accessing a resource.
 * @prop {string} [roleOperation] - The required role operation for accessing a resource.
 */
function AuthorizationChecker({ children, roleFunctionality, roleOperation }) {
  const { currentUser } = useAuth();
  const location = useLocation();

  if (!currentUser.user && location.pathname !== '/public/assistant') {
    // Redirect them to the /signIn page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they signIn, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/signIn" state={{ from: location }} replace />;
  }

  const isUserUnauthorized =
    currentUser &&
    roleFunctionality &&
    roleOperation &&
    !currentUser.isAuthorized(roleFunctionality, roleOperation);
  if (isUserUnauthorized) {
    return (
      <Navigate
        to="/error"
        state={{
          messageId: 'error.message.insufficient.right',
          titleId: 'error.title.insufficient.right',
          code: 403
        }}
      />
    );
  }

  return children;
}

/**
 * ArcadeRoutes component is responsible for setting up and rendering the routes for the application.
 * It sets up both the portal routes and the standalone routes, using the passed routes configuration.
 *
 * @component
 * @prop {Object[]} routesConf - The configuration for the routes. Each object should have the path and other related info.
 */
const DigiAiRoutes = memo(({ routes }) => {
  const { formatMessage: msg } = useIntl();
  const currentAppCode = getUrlParameter(window.location, app.url.paramApp)?.toUpperCase();
  const applicationRole = currentAppCode ? `APPLICATION_${currentAppCode}` : null;
  const mainReactRoutes = buildRoutes(routes.mainRoutes, applicationRole, ROLE_OPERATION_READ);
  const adminReactRoutes = buildRoutes(routes.adminRoutes, applicationRole, ROLE_OPERATION_READ);
  const knowledgeBaseReactRoutes = buildRoutes(
    routes.knowledgeBaseRoutes,
    applicationRole,
    ROLE_OPERATION_READ
  );
  const standaloneRoutes = routes.allRoutes.filter((x) => x.standalone);
  const standaloneReactRoutes = buildRoutes(standaloneRoutes, applicationRole, ROLE_OPERATION_READ);

  let indexPath = '/portal/admin/home';
  if (routes.allRoutes?.length > 0) {
    const firstRoute = routes.allRoutes.at(0);
    indexPath = `/portal/${firstRoute.path.replace(':view', firstRoute.view)}`;
  }

  const location = useLocation();

  let menuRoutes = [];
  if (location.pathname.startsWith('/portal/admin/')) {
    menuRoutes = routes.adminRoutes;
  } else if (location.pathname.startsWith('/portal/knowledgeBase/')) {
    menuRoutes = routes.knowledgeBaseRoutes;
  }

  routes.allRoutes.forEach((route) => {
    const label = msg({ id: `page.${route.key}`, defaultMessage: route.name });

    const isActiveRoute = location.pathname.includes(route.path);
    if (isActiveRoute) {
      document.title = msg({ id: 'product.name' }) + ' - ' + label;
    }
  });

  return (
    <Routes>
      <Route
        path="/portal"
        element={
          <AuthorizationChecker roleFunctionality={RoleEnum.USER} roleOperation="read">
            <SidenavTemplate
              layout="dashboard"
              navigationRoutes={routes.mainRoutes}
              menuRoutes={menuRoutes}
            />
          </AuthorizationChecker>
        }
      >
        {mainReactRoutes}
        <Route path="admin/*">
          <Route index element={<Navigate to="home" replace />} />
          {adminReactRoutes}
        </Route>
        <Route path="knowledgeBase/:knowledgeBaseId/*">{knowledgeBaseReactRoutes}</Route>
        <Route
          index
          path="*"
          element={<SidenavTemplate layout="dashboard" navigationRoutes={routes.mainRoutes} />}
        />
      </Route>
      <Route
        path="/standalone"
        element={<StandaloneTemplate routeConfigurations={standaloneRoutes} />}
      >
        {standaloneReactRoutes}
      </Route>
      <Route path="public/assistant" element={<ChatBoxPublic />} />
      <Route path="signIn" element={<SignIn />} />
      <Route path="error" element={<Error />} />
      <Route path="*" element={<Navigate to={indexPath} />} />
    </Routes>
  );

  /**
   * buildRoutes function is a recursive utility that maps the route configurations to Route components.
   *
   * @function
   * @param {Object[]} allRoutes - The list of route configurations.
   * @param {string} applicationRole - The application role for authorization checking.
   * @param {string} roleOperation - The role operation for authorization checking.
   * @returns {ReactNode[]} - An array of Route components.
   */
  function buildRoutes(allRoutes, applicationRole, roleOperation) {
    return allRoutes.flatMap((route) => {
      if (route.collapse) {
        return buildRoutes(route.collapse, applicationRole, roleOperation);
      }

      if (route.path) {
        return (
          <Route
            key={route.key}
            path={route.path}
            element={
              <AuthorizationChecker
                roleFunctionality={route.functionality}
                roleOperation={roleOperation}
              >
                {route.component}
              </AuthorizationChecker>
            }
          />
        );
      }

      return [];
    });
  }
});

DigiAiRoutes.propTypes = {
  routes: PropTypes.shape({
    allRoutes: PropTypes.array.isRequired,
    mainRoutes: PropTypes.array.isRequired,
    adminRoutes: PropTypes.array.isRequired,
    knowledgeBaseRoutes: PropTypes.array.isRequired
  }).isRequired
};

export default DigiAiRoutes;
